import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
  ViewEncapsulation,
  computed,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TemplatePortal } from '@angular/cdk/portal';
import { InfoPopoverComponent } from '../../info-popover/info-popover.component';
import { isHelpPosition, resolveHelpText } from '../sb-field-help';
import { boundDay, boundValueDay, dayNumber, formatHint, formatLongDay, formatRangeDays, isoDate, sameDay } from './sb-date-time-format';
import { SbDateParts } from './sb-date-time-parse';
import { SbDateTimeOverlay, coarsePointer, focusAfter, isRepeatClick, narrowViewport } from './sb-date-time-overlay';
import { SbDateTimePanelComponent } from './sb-date-time-panel.component';
import { SbDateTimeFieldComponent, SbFieldEdit } from './sb-date-time-field.component';
import { FieldState, cleared, emptyState, fromValue, message, notice, partsShown, reading, withParts } from './sb-date-time-segments';
import { SbPickView, SbRangePresetSet, computeRangePresets } from './sb-quick-picks';

export interface SbDateRange {
  start: string | null;
  end: string | null;
}

export type SbDateRangeHalf = 'start' | 'end';
type Half = SbDateRangeHalf;

let nextId = 0;

/**
 * A date range for filters: From and To in one field group, each an optional segmented date, with
 * one panel (presets and one month), one clear button and one `rangeChange` per commit. Bounds are
 * `YYYY-MM-DD` or null (no bound). Not a form control: the filter pages bind it to properties.
 *
 * A commit happens when focus leaves both halves, their buttons and the panel, on Enter, a preset,
 * the second calendar click or Clear; it emits only when a bound changed and both halves read. A
 * start after the end is emitted too, with a message under To: the page decides what such a range
 * filters, as it did with two separate fields.
 */
@Component({
  selector: 'app-sb-input-date-range',
  templateUrl: './sb-input-date-range.component.html',
  styleUrls: ['./sb-date-time-control.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SbDateTimePanelComponent, SbDateTimeFieldComponent, InfoPopoverComponent],
})
export class SbInputDateRangeComponent implements OnChanges, OnDestroy {
  readonly start = model<string | null>(null);
  readonly end = model<string | null>(null);
  readonly rangeChange = output<SbDateRange>();
  readonly label = input('');
  readonly startLabel = input('From');
  readonly endLabel = input('To');
  readonly ariaLabel = input('');
  readonly description = input('');
  readonly help = input('');
  /** @deprecated Prefer `help`. */
  readonly info = input('');
  readonly disabled = input(false);
  readonly error = input('');
  readonly id = input(`sb-date-range-${++nextId}`);
  readonly min = input<string | Date | null>(null);
  readonly max = input<string | Date | null>(null);
  readonly presets = input<SbRangePresetSet>('past');
  readonly timeZone = input<string | null>(null);

  private readonly injector = inject(Injector);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly announcer = inject(LiveAnnouncer);
  private readonly fields = viewChildren(SbDateTimeFieldComponent);
  private readonly triggerEl = viewChild<ElementRef<HTMLButtonElement>>('triggerEl');
  private readonly groupEl = viewChild.required<ElementRef<HTMLElement>>('groupEl');
  private readonly labelRow = viewChild<ElementRef<HTMLElement>>('labelRow');
  private readonly panelTpl = viewChild.required<TemplateRef<unknown>>('panelTpl');
  private readonly panel = viewChild(SbDateTimePanelComponent);
  private readonly overlay = new SbDateTimeOverlay(this.injector);
  /** The model values the halves were last shown for, so a `minMax` value is kept until edited. */
  private shown: SbDateRange = { start: null, end: null };

  readonly halves: readonly Half[] = ['start', 'end'];
  readonly startState = signal<FieldState>(emptyState('date'));
  readonly endState = signal<FieldState>(emptyState('date'));
  /** The half focus goes back to when the panel closes. */
  private lastHalf: Half = 'start';
  readonly isOpen = signal(false);
  readonly picks = signal<SbPickView[]>([]);
  readonly narrow = signal(false);
  /** Each half whose unfinished entry was reported (the range was left) and not edited since. */
  private readonly reported = signal<Record<Half, boolean>>({ start: false, end: false });

  readonly startParts = computed(() => partsShown(this.startState()).date);
  readonly endParts = computed(() => partsShown(this.endState()).date);
  readonly minDay = computed(() => boundDay(this.min()));
  readonly maxDay = computed(() => boundDay(this.max()));
  readonly labelId = computed(() => `${this.id()}-label`);
  readonly panelId = computed(() => `${this.id()}-panel`);
  readonly hintId = computed(() => `${this.id()}-hint`);
  readonly interactive = computed(() => !this.disabled());
  readonly hint = computed(() => formatHint('range', this.interactive()));
  readonly helpText = computed(() => resolveHelpText(this.help(), this.info()));
  readonly showHelpAfterLabel = computed(() => isHelpPosition(this.helpText(), 'afterLabel', 'afterLabel'));
  readonly hasValue = computed(() => [this.startState(), this.endState()].some((state) => reading(state).kind !== 'empty'));

  /** The trigger's name, with the range it holds: "Change dates, Oct 1 – Oct 28, 2026". */
  readonly triggerLabel = computed(() => {
    const days = formatRangeDays(this.completeDay('start'), this.completeDay('end'));
    return days ? `Change dates, ${days}` : 'Choose dates';
  });

  readonly startError = computed(() => this.halfError('start'));
  readonly endError = computed(() => this.halfError('end') || this.orderError());
  readonly groupError = computed(() => this.error() || '');
  readonly startNotice = computed(() => this.halfNotice('start'));
  readonly endNotice = computed(() => this.halfNotice('end'));

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['start'] && this.start() !== this.shown.start) {
      this.showHalf('start', this.start());
    }
    if (changes['end'] && this.end() !== this.shown.end) {
      this.showHalf('end', this.end());
    }
    if (changes['disabled'] && this.disabled()) {
      this.close(false);
    }
  }

  ngOnDestroy(): void {
    this.overlay.destroy();
  }

  stateOf(half: Half): FieldState {
    return half === 'start' ? this.startState() : this.endState();
  }

  setState(half: Half, state: FieldState): void {
    (half === 'start' ? this.startState : this.endState).set(state);
  }

  labelFor(half: Half): string {
    return half === 'start' ? this.startLabel() : this.endLabel();
  }

  errorFor(half: Half): string {
    return half === 'start' ? this.startError() : this.endError();
  }

  noticeFor(half: Half): string {
    return half === 'start' ? this.startNotice() : this.endNotice();
  }

  hasHalfValue(half: Half): boolean {
    const state = this.stateOf(half);
    return state.segs.some((seg) => seg.value !== null || !!seg.typed || !!seg.refused);
  }

  describedBy(half: Half): string {
    return [
      this.hintId(),
      this.description() ? `${this.id()}-description` : '',
      this.errorFor(half) ? `${this.id()}-${half}-error` : '',
      this.groupError() ? `${this.id()}-error` : '',
    ]
      .filter(Boolean)
      .join(' ');
  }

  /**
   * A key, paste or undo in a half. From typed whole, from its first segment on, moves on to To's
   * month. Ctrl+Z or Ctrl+Y over a Clear, preset or range pick takes the other half with it, so
   * undo never makes a one-sided range nobody had.
   */
  onEdited(half: Half, edit: SbFieldEdit): void {
    if (edit.state.focus >= 0) {
      this.lastHalf = half;
    }
    if (edit.changed && this.reported()[half]) {
      this.reported.update((reported) => ({ ...reported, [half]: false }));
    }
    if (edit.crossed) {
      this.fieldFor(half === 'start' ? 'end' : 'start')?.follow(edit.crossed.step, edit.crossed.direction);
    }
    if (half === 'start' && edit.filled && reading(edit.state).kind === 'complete') {
      this.fieldFor('end')?.enterByTyping();
    }
  }

  onKeydown(event: KeyboardEvent): void {
    // The Enter or Esc that ends an input method's composition is the input method's, not the range's.
    if (!this.interactive() || event.isComposing || event.keyCode === 229) {
      return;
    }
    if (event.key === 'Enter') {
      this.commit();
      if (this.isOpen()) {
        event.preventDefault();
        event.stopPropagation();
        this.close(false);
      }
      return;
    }
    if (event.key === 'Escape' && this.isOpen()) {
      event.preventDefault();
      event.stopPropagation();
      this.close(false);
    }
  }

  /** The second click of a double-click on the button is the same gesture as the first: it changes nothing. */
  onTriggerClick(event: MouseEvent): void {
    if (isRepeatClick(event)) {
      return;
    }
    if (this.isOpen()) {
      this.close(true);
      return;
    }
    this.open('keyboard');
  }

  /**
   * Moving between the two halves (six segments) or into the panel is not a commit; leaving them
   * all is. Focus that drops to nothing while the panel is open is the calendar redrawing another month.
   */
  onFocusOut(event: FocusEvent): void {
    const next = event.relatedTarget as Node | null;
    if (next ? this.host.nativeElement.contains(next) || this.overlay.contains(next) : this.isOpen()) {
      return;
    }
    this.reported.set({ start: true, end: true });
    this.commit();
    this.fields().forEach((field) => field.endVisit());
    this.close(false);
  }

  onPanelMouseDown(event: MouseEvent): void {
    if (!(event.target as HTMLElement).closest('button, [tabindex], input, [role="option"]')) {
      event.preventDefault();
    }
  }

  /** Focus goes back to a half (the trigger after touch): the clear button itself disappears. */
  clear(): void {
    this.setBoth(null, null);
    this.commit();
    if (this.isOpen()) {
      this.close(true);
      return;
    }
    this.refocus();
  }

  open(by: 'pointer' | 'keyboard'): void {
    if (!this.interactive()) {
      return;
    }
    if (!this.isOpen()) {
      this.picks.set(computeRangePresets(this.presets(), { now: Date.now(), zone: this.timeZone(), min: this.minDay(), max: this.maxDay() }));
      this.narrow.set(narrowViewport());
      this.overlay.open(
        this.groupEl().nativeElement,
        new TemplatePortal(this.panelTpl(), this.viewContainerRef),
        (target) => {
          if (!this.host.nativeElement.contains(target)) {
            this.commit();
            this.close(false);
          }
        },
        // A scroll that moves the group closes the panel; focus that was in it goes back to a half.
        () => this.close(this.overlay.contains(document.activeElement), true),
        (narrow) => this.narrow.set(narrow),
        // Above the halves, the panel leaves the group's label in view.
        this.labelRow()?.nativeElement ?? null,
      );
      this.isOpen.set(true);
    }
    if (by === 'keyboard') {
      setTimeout(() => this.panel()?.focusInitial());
    }
  }

  /** The panel goes while it still counts as open: the focus Chrome drops from it as it goes ends no visit. */
  close(refocus: boolean, scrolled = false): void {
    if (!this.isOpen()) {
      return;
    }
    this.overlay.close();
    this.isOpen.set(false);
    if (refocus) {
      this.refocus(scrolled);
    }
  }

  onRangeChosen(range: { start: SbDateParts; end: SbDateParts }): void {
    this.setBoth(range.start, range.end);
    this.commit();
    this.announcer.announce(`Selected ${formatLongDay(range.start)} to ${formatLongDay(range.end)}`, 'polite');
    this.close(true);
  }

  onPresetChosen(view: SbPickView): void {
    this.setBoth(view.result?.date ?? null, view.result?.end ?? null);
    this.commit();
    this.announcer.announce(`Selected ${view.label}${view.value ? `, ${view.value}` : ''}`, 'polite');
    this.close(true);
  }

  onTabbedOut(direction: 'forward' | 'back'): void {
    const trigger = this.triggerEl()?.nativeElement;
    const panel = this.overlay.element;
    this.commit();
    this.close(false);
    if (!trigger) {
      return;
    }
    if (direction === 'back') {
      trigger.focus();
      return;
    }
    focusAfter(trigger, panel);
  }

  /**
   * Writes both models and emits once, when both halves read (or are empty) and a bound changed.
   * A half the user did not edit keeps its model value as it was, so a longer `YYYY-MM-DD…` value
   * is not rewritten.
   */
  commit(): void {
    const start = this.halfValue('start');
    const end = this.halfValue('end');
    if (start === undefined || end === undefined) {
      return;
    }
    if (start === this.start() && end === this.end()) {
      return;
    }
    this.shown = { start, end };
    this.start.set(start);
    this.end.set(end);
    this.rangeChange.emit({ start, end });
  }

  private completeDay(half: Half): SbDateParts | null {
    const read = reading(this.stateOf(half));
    return read.kind === 'complete' ? read.date : null;
  }

  private halfValue(half: Half): string | null | undefined {
    const read = reading(this.stateOf(half));
    const value = half === 'start' ? this.start() : this.end();
    if (read.kind === 'empty') {
      return null;
    }
    if (read.kind !== 'complete' || !read.date) {
      return undefined;
    }
    return value && sameDay(boundValueDay(value), read.date) ? value : isoDate(read.date);
  }

  private halfFocused(half: Half): boolean {
    return this.fieldFor(half)?.focused() ?? false;
  }

  private halfError(half: Half): string {
    // An unfinished half says so once the range was left, and still when focus comes back to fix
    // it; an entry typed since says nothing until the range is left again.
    return message(this.stateOf(half), this.halfFocused(half), this.reported()[half]);
  }

  private halfNotice(half: Half): string {
    return notice(this.stateOf(half), this.halfFocused(half));
  }

  private orderError(): string {
    const start = this.completeDay('start');
    const end = this.completeDay('end');
    return start && end && dayNumber(start) > dayNumber(end) ? 'Invalid range: the start is after the end' : '';
  }

  private showHalf(half: Half, value: string | null): void {
    this.shown = { ...this.shown, [half]: value };
    const date = boundValueDay(value);
    const state = fromValue(date ? isoDate(date) : '', 'date');
    const field = this.fieldFor(half);
    if (field) {
      field.reset(state);
    } else {
      this.setState(half, state);
    }
  }

  /** A pick, a preset or Clear sets both halves as one step: Ctrl+Z in either brings back what both held before. */
  private setBoth(start: SbDateParts | null, end: SbDateParts | null): void {
    const step = {};
    this.setHalf('start', start, step);
    this.setHalf('end', end, step);
  }

  private setHalf(half: Half, date: SbDateParts | null, step: object): void {
    const current = this.stateOf(half);
    const next = date ? withParts(current, date, null) : cleared(current);
    const field = this.fieldFor(half);
    if (field) {
      field.apply(next, step);
    } else {
      this.setState(half, next);
    }
  }

  private refocus(preventScroll = false): void {
    const trigger = this.triggerEl()?.nativeElement;
    if (coarsePointer() && trigger) {
      trigger.focus({ preventScroll });
      return;
    }
    this.fieldFor(this.lastHalf)?.focus('last', preventScroll);
  }

  private fieldFor(half: Half): SbDateTimeFieldComponent | undefined {
    return this.fields()[half === 'start' ? 0 : 1];
  }
}
